// ──────────────────────────────────────────────
// API Hooks — Fetches from backend, transforms to frontend types.
// Replaces Phase 2 mock data hooks with real API calls.
// ──────────────────────────────────────────────

import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import type {
  Commander,
  Opportunity,
  RevenueStream,
  RevenueEntry,
  Venture,
  Milestone,
  AdvantageProfile,
  ScanLog,
  ScanSourceDetail,
  Achievement,
  DeepDiveMessage,
  Briefing,
  RawSignalRecord,
  PainCluster,
  PainAnalytics,
  DeepDiveSection,
} from "@/lib/mock-data";

// Re-export types so existing imports keep working
export type {
  Commander,
  Opportunity,
  RevenueStream,
  RevenueEntry,
  Venture,
  Milestone,
  AdvantageProfile,
  ScanLog,
  ScanSourceDetail,
  Achievement,
  DeepDiveMessage,
  Briefing,
  RawSignalRecord,
  PainCluster,
  PainAnalytics,
  DeepDiveSection,
};

// Generic fetch hook
function useApi<T>(path: string | null, defaultValue: T): { data: T; isLoading: boolean; refetch: () => void } {
  const [data, setData] = useState<T>(defaultValue);
  const [isLoading, setIsLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!path) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    api
      .get<T>(path)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        /* errors handled by api.ts (401 redirect) */
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [path, tick]);

  return { data, isLoading, refetch };
}

// ── Helpers to transform backend decimal strings → numbers ──

function parseNum(val: unknown): number {
  if (val == null) return 0;
  if (typeof val === "number") return val;
  if (typeof val === "string") return parseFloat(val) || 0;
  return 0;
}

function toOpportunity(raw: Record<string, unknown>): Opportunity {
  return {
    id: raw.id as string,
    title: raw.title as string,
    description: (raw.description as string) ?? "",
    sourceType: raw.sourceType as Opportunity["sourceType"],
    sourceUrl: (raw.sourceUrl as string) ?? "",
    sourceCommunity: (raw.sourceCommunity as string) ?? "",
    industry: (raw.industry as string) ?? "",
    status: raw.status as Opportunity["status"],
    compositeScore: parseNum(raw.compositeScore),
    painIntensity: parseNum(raw.painIntensity),
    purchasingPower: parseNum(raw.purchasingPower),
    targetability: parseNum(raw.targetability),
    marketGrowth: parseNum(raw.marketGrowth),
    whitespace: parseNum(raw.whitespace),
    monopolyPotential: parseNum(raw.monopolyPotential),
    domainProximity: parseNum(raw.domainProximity),
    distributionAdvantage: parseNum(raw.distributionAdvantage),
    buildFeasibility: parseNum(raw.buildFeasibility),
    speedToRevenue: parseNum(raw.speedToRevenue),
    rawSignals: (raw.rawSignals as Opportunity["rawSignals"]) ?? [],
    competitiveLandscape: (raw.competitiveLandscape as Opportunity["competitiveLandscape"]) ?? [],
    aiAnalysis: (raw.aiAnalysis as string) ?? "",
    ventureId: (raw.ventureId as string) ?? null,
    discoveredAt: raw.discoveredAt as string,
    scoredAt: raw.scoredAt as string,
    // V2 fields
    opportunityName: (raw.opportunityName as string) ?? null,
    elevatorPitch: (raw.elevatorPitch as string) ?? null,
    subIndustry: (raw.subIndustry as string) ?? null,
    painSummary: (raw.painSummary as string) ?? null,
    proposedSolution: (raw.proposedSolution as string) ?? null,
    saturationLevel: (raw.saturationLevel as Opportunity["saturationLevel"]) ?? null,
    saturationDetail: (raw.saturationDetail as string) ?? null,
    existingCompetitors: (raw.existingCompetitors as Opportunity["existingCompetitors"]) ?? null,
    blueOceanAngle: (raw.blueOceanAngle as string) ?? null,
    signalCount: parseNum(raw.signalCount) || null,
    sourcePlatforms: (raw.sourcePlatforms as string[]) ?? null,
    confidence: (raw.confidence as Opportunity["confidence"]) ?? null,
    clusterIds: (raw.clusterIds as string[]) ?? null,
    legacy: (raw.legacy as boolean) ?? false,
  };
}

function toRevenueStream(raw: Record<string, unknown>): RevenueStream {
  return {
    id: raw.id as string,
    name: raw.name as string,
    targetMonthly: parseNum(raw.targetMonthly),
    targetClients: parseNum(raw.targetClients),
    avgDealSizeLow: parseNum(raw.avgDealSizeLow),
    avgDealSizeHigh: parseNum(raw.avgDealSizeHigh),
    isActive: raw.isActive as boolean,
    leverageLevel: raw.leverageLevel as RevenueStream["leverageLevel"],
    sortOrder: parseNum(raw.sortOrder),
  };
}

function toVenture(raw: Record<string, unknown>): Venture {
  return {
    id: raw.id as string,
    opportunityId: (raw.opportunityId as string) ?? null,
    name: raw.name as string,
    description: (raw.description as string) ?? "",
    status: raw.status as Venture["status"],
    githubUrl: (raw.githubUrl as string) ?? "",
    liveUrl: (raw.liveUrl as string) ?? "",
    monthlyRevenue: parseNum(raw.monthlyRevenue),
    launchDate: (raw.launchDate as string) ?? null,
  };
}

function toMilestone(raw: Record<string, unknown>): Milestone {
  return {
    id: raw.id as string,
    title: raw.title as string,
    description: (raw.description as string) ?? "",
    metricType: raw.metricType as Milestone["metricType"],
    targetValue: parseNum(raw.targetValue),
    currentValue: parseNum(raw.currentValue),
    targetDate: (raw.targetDate as string) ?? "",
    status: raw.status as Milestone["status"],
  };
}

// ── Hooks ──

export function useCommander() {
  const defaultCommander: Commander = {
    id: "",
    email: "",
    name: "",
    loginStreak: 0,
    xpTotal: 0,
    lastLoginAt: "",
  };
  return useApi<Commander>("/api/auth/me", defaultCommander);
}

export function useBriefing() {
  const defaultBriefing: Briefing = {
    greeting: "",
    revenue: { currentMonth: 0, target: 83333, percentage: 0, gap: 83333 },
    overnightScan: { newOpportunities: 0, topScore: 0, topOpportunityTitle: "" },
    topActions: [],
    strangeloveQuote: "",
  };
  return useApi<Briefing>("/api/briefing", defaultBriefing);
}

export function useOpportunities(filters?: {
  tier?: string;
  industry?: string;
  status?: string;
  sort?: string;
  saturation?: string;
  confidence?: string;
}) {
  const params = new URLSearchParams();
  if (filters?.tier) params.set("tier", filters.tier);
  if (filters?.industry) params.set("industry", filters.industry);
  if (filters?.status) params.set("status", filters.status);
  if (filters?.sort) params.set("sort", filters.sort);
  if (filters?.saturation) params.set("saturation", filters.saturation);
  if (filters?.confidence) params.set("confidence", filters.confidence);
  const qs = params.toString();
  const path = `/api/opportunities${qs ? `?${qs}` : ""}`;

  const [data, setData] = useState<Opportunity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    api
      .get<{ data: Record<string, unknown>[]; total: number }>(path)
      .then((res) => {
        if (!cancelled) setData(res.data.map(toOpportunity));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [path, tick]);

  return { data, isLoading, refetch };
}

export function useOpportunity(id: string) {
  const [data, setData] = useState<Opportunity | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    api
      .get<Record<string, unknown>>(`/api/opportunities/${id}`)
      .then((res) => {
        if (!cancelled) setData(toOpportunity(res));
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [id]);

  return { data, isLoading };
}

export function useDeepDiveMessages(opportunityId: string) {
  return useApi<DeepDiveMessage[]>(`/api/opportunities/${opportunityId}/messages`, []);
}

export function useRevenueStreams() {
  const { data: raw, isLoading, refetch } = useApi<Record<string, unknown>[]>("/api/revenue/streams", []);
  return { data: raw.map(toRevenueStream), isLoading, refetch };
}

export function useRevenueEntries(filters?: { streamId?: string; dateRange?: string }) {
  const params = new URLSearchParams();
  if (filters?.streamId) params.set("streamId", filters.streamId);
  const qs = params.toString();
  const path = `/api/revenue/entries${qs ? `?${qs}` : ""}`;

  const { data: streams } = useRevenueStreams();
  const { data: raw, isLoading, refetch } = useApi<Record<string, unknown>[]>(path, []);

  const streamNameMap = new Map(streams.map((s) => [s.id, s.name]));

  const entries: RevenueEntry[] = raw.map((e) => ({
    id: e.id as string,
    streamId: e.streamId as string,
    streamName: streamNameMap.get(e.streamId as string) ?? "",
    amount: parseNum(e.amount),
    clientName: (e.clientName as string) ?? "",
    description: (e.description as string) ?? "",
    date: e.date as string,
    isRecurring: e.isRecurring as boolean,
  }));

  return { data: entries, isLoading, refetch };
}

export function useRevenueSummary() {
  const { data: raw, isLoading, refetch } = useApi<{
    currentMonth: number;
    target: number;
    percentage: number;
    gap: number;
    byStream: { streamId: string; total: number }[];
    trend: { month: string; revenue: number; target: number }[];
  }>("/api/revenue/summary", {
    currentMonth: 0,
    target: 83333,
    percentage: 0,
    gap: 83333,
    byStream: [],
    trend: [],
  });

  return { data: raw, isLoading, refetch };
}

export function useVentures() {
  const { data: raw, isLoading, refetch } = useApi<Record<string, unknown>[]>("/api/ventures", []);
  return { data: raw.map(toVenture), isLoading, refetch };
}

export function useMilestones() {
  const { data: raw, isLoading, refetch } = useApi<Record<string, unknown>[]>("/api/milestones", []);
  return { data: raw.map(toMilestone), isLoading, refetch };
}

export function useAdvantageProfile() {
  const defaultProfile: AdvantageProfile = {
    industriesKnown: [],
    skills: [],
    distributionChannels: [],
    productsShipped: [],
    budgetMonthlyMax: 0,
  };
  const { data: raw, isLoading, refetch } = useApi<Record<string, unknown>>("/api/profile", {});

  const profile: AdvantageProfile = raw.id
    ? {
        industriesKnown: (raw.industriesKnown as AdvantageProfile["industriesKnown"]) ?? [],
        skills: (raw.skills as string[]) ?? [],
        distributionChannels: (raw.distributionChannels as AdvantageProfile["distributionChannels"]) ?? [],
        productsShipped: (raw.productsShipped as AdvantageProfile["productsShipped"]) ?? [],
        budgetMonthlyMax: parseNum(raw.budgetMonthlyMax),
      }
    : defaultProfile;

  return { data: profile, isLoading, refetch };
}

export function useScanLogs() {
  const { data: raw, isLoading, refetch } = useApi<{ logs: ScanLog[] }>("/api/scans/history", { logs: [] });
  return { data: raw.logs || [], isLoading, refetch };
}

export interface ScanSource {
  id: string;
  name: string;
  description: string | null;
  platform: string;
  sourceType: "pain_scanner" | "builder_intel" | "trend_signal" | "manual";
  enabled: boolean;
  config: Record<string, unknown> | null;
  requiresEnvKey: string | null;
  phase: number;
  sortOrder: number;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  lastRunCount: number | null;
}

export function useScanSources() {
  const { data: raw, isLoading, refetch } = useApi<{ sources: ScanSource[] }>("/api/scan-sources", { sources: [] });
  return { data: raw.sources || [], isLoading, refetch };
}

export function useAchievements() {
  return useApi<Achievement[]>("/api/achievements", []);
}

export function useDashboardStats() {
  return useApi<{
    totalRevenueThisMonth: number;
    opportunityCount: number;
    strikeNowCount: number;
    winnerCount: number;
    streak: number;
    xp: number;
  }>("/api/dashboard/stats", {
    totalRevenueThisMonth: 0,
    opportunityCount: 0,
    strikeNowCount: 0,
    winnerCount: 0,
    streak: 0,
    xp: 0,
  });
}

// ── V2 Hooks ──

export function useRawSignals(filters?: {
  industry?: string;
  platform?: string;
  status?: string;
  classification?: string;
  page?: number;
}) {
  const params = new URLSearchParams();
  if (filters?.industry) params.set("industry", filters.industry);
  if (filters?.platform) params.set("platform", filters.platform);
  if (filters?.status) params.set("status", filters.status);
  if (filters?.classification) params.set("classification", filters.classification);
  if (filters?.page) params.set("page", String(filters.page));
  const qs = params.toString();
  const path = `/api/raw-signals${qs ? `?${qs}` : ""}`;

  return useApi<{ data: RawSignalRecord[]; total: number }>(path, { data: [], total: 0 });
}

export function usePainClusters(filters?: { status?: string; industry?: string }) {
  const params = new URLSearchParams();
  if (filters?.status) params.set("status", filters.status);
  if (filters?.industry) params.set("industry", filters.industry);
  const qs = params.toString();
  const path = `/api/pain-clusters${qs ? `?${qs}` : ""}`;

  return useApi<{ data: PainCluster[] }>(path, { data: [] });
}

export function usePainAnalytics() {
  return useApi<PainAnalytics>("/api/pain-analytics", {
    totalSignals: 0,
    totalClusters: 0,
    qualifiedClusters: 0,
    synthesizedOpps: 0,
    industryBreakdown: [],
  });
}

export function useDeepDiveSection(oppId: string, section: string) {
  const path = section ? `/api/opportunities/${oppId}/deep-dive/${section}` : null;
  return useApi<{ data: DeepDiveSection | null }>(path, { data: null });
}

export function useEvidence(oppId: string) {
  return useApi<{ data: RawSignalRecord[] }>(`/api/opportunities/${oppId}/evidence`, { data: [] });
}
